/**
 * Live HTTP implementation of the B1.7 Cashier feature API.
 *
 * Targets the Phase C2 backend surface at `/api/biz/cashier/*` +
 * `/api/biz/cashier/orders/*` (see `backend/src/api/business/biz-cashier-routes.ts`).
 *
 * Contract alignment strategy:
 *   The backend's {@link CashierOrder} shape is a **superset** of the FE
 *   contract (extra fields: branchId, currency, invoiceId, paidAt, voidedAt,
 *   voidedReason, line.lineNo, line.kind, line.sku, line.unit, line.taxMinor,
 *   line.itemId nullable). The backend session row likewise exposes
 *   openedByActorId + closedByActorId etc. which the FE does not consume.
 *
 *   Instead of pushing the BE superset through the FE types, this module
 *   adapts each response into the frozen `CashierSnapshot` shape:
 *     • `itemId: null` → `''` (FE requires non-null for line keying)
 *     • `openedByName` derived from staff/actor id fallbacks
 *     • Extra fields are dropped (explicit projection — no `as` casts)
 *
 * Branch cache (module-local):
 *   The FE's public mutation shapes (`AddCartItemRequest`, `ParkOrderRequest`,
 *   …) intentionally do NOT carry `orderId`/`sessionId`. The HTTP layer
 *   derives those from the most recent snapshot returned for a branch,
 *   cached keyed by `branchId`. This keeps `useCashier` and
 *   `CashierPage.tsx` untouched during the mock→live flip.
 *
 * Catalog (Phase F / P1a):
 *   The BE now exposes `GET /api/biz/cashier/catalog?branchId=` (projection
 *   over `biz_services` + `biz_products`). `fetchSnapshot` runs the snapshot
 *   and catalog requests in parallel and folds them into a single
 *   `CashierSnapshot`. A short-lived per-branch cache (60s) avoids hitting
 *   the catalog endpoint on every cart mutation refresh — catalogs change
 *   slowly while session/order state churns by the second.
 *
 * Idempotency:
 *   `commitTender` generates a deterministic key `biz:cashier:commit:<orderId>`
 *   per order; the backend ON CONFLICT guard rejects duplicates after the
 *   first success.
 *
 * @see cashier-api-mock.ts (fixture path)
 * @see cashier-api.ts (mock↔live dispatcher)
 * @see backend/src/api/business/biz-cashier-routes.ts
 */

import { ApiError } from '@/shared/api/apiClient';
import { bizDelete, bizGet, bizPatch, bizPost } from '@/shared/api/bizClient';

import type {
  AddCartItemRequest,
  ApplyDiscountRequest,
  CashierCatalogCategory,
  CashierCatalogItem,
  CashierOrder,
  CashierOrderLine,
  CashierOrderLineDiscount,
  CashierPaymentMethod,
  CashierSession,
  CashierSnapshot,
  CashierTender,
  CloseSessionRequest,
  CommitTenderRequest,
  CommitTenderResult,
  GetCashierSnapshotParams,
  ParkOrderRequest,
  RemoveCartItemRequest,
  ResumeParkedRequest,
  UpdateCartQtyRequest,
  VoidOrderRequest,
} from './types';

// ---------------------------------------------------------------------------
// Branch-scoped cache (session + active order derivation for mutations)
// ---------------------------------------------------------------------------

interface BranchContext {
  sessionId: string;
  activeOrderId: string;
}

const branchCtxCache = new Map<string, BranchContext>();

function rememberBranchCtx(branchId: string, ctx: BranchContext): void {
  branchCtxCache.set(branchId, ctx);
}

// ---------------------------------------------------------------------------
// Catalog cache (per-branch, 60s TTL)
//
// Catalog rows change slowly (admin-edited services + retail SKUs) while POS
// snapshot refreshes happen on every cart mutation. Hitting `/biz/cashier/catalog`
// on each refresh would multiply network traffic for no gain. The TTL gets
// invalidated by the explicit `invalidateCatalogCache` helper if the SPA later
// wants to force a refresh after a service-edit screen returns.
// ---------------------------------------------------------------------------

const CATALOG_CACHE_TTL_MS = 60_000;

interface CatalogCacheEntry {
  readonly fetchedAt: number;
  readonly payload: CatalogPayloadResponse;
}

const catalogCache = new Map<string, CatalogCacheEntry>();

interface CatalogPayloadResponse {
  readonly branchId: string;
  readonly categories: readonly CashierCatalogCategory[];
  readonly catalog: readonly CashierCatalogItem[];
  readonly availableTenders: readonly CashierPaymentMethod[];
  readonly generatedAt: string;
}

async function fetchCatalog(
  branchId: string,
  options?: { force?: boolean },
): Promise<CatalogPayloadResponse> {
  const cached = catalogCache.get(branchId);
  if (
    !options?.force &&
    cached &&
    Date.now() - cached.fetchedAt < CATALOG_CACHE_TTL_MS
  ) {
    return cached.payload;
  }
  const payload = await bizGet<CatalogPayloadResponse>('/biz/cashier/catalog', {
    params: { branchId },
  });
  catalogCache.set(branchId, { fetchedAt: Date.now(), payload });
  return payload;
}

/** Test/QA helper — drop the cached catalog so the next snapshot refetches. */
export function invalidateCatalogCache(branchId?: string): void {
  if (branchId) {
    catalogCache.delete(branchId);
    return;
  }
  catalogCache.clear();
}

function requireBranchCtx(branchId: string | null | undefined): BranchContext {
  if (!branchId) {
    throw new ApiError(
      {
        type: 'petota:biz-cashier/branch-not-ready',
        title: 'Cashier branch context missing',
        status: 409,
        detail:
          'Cashier snapshot has not been loaded yet for this branch. Call getCashierSnapshot first.',
      },
      409,
    );
  }
  const ctx = branchCtxCache.get(branchId);
  if (!ctx) {
    throw new ApiError(
      {
        type: 'petota:biz-cashier/snapshot-required',
        title: 'Cashier snapshot required before mutations',
        status: 409,
        detail:
          'The cashier mutation flow needs a prior snapshot load to resolve session/order ids for this branch.',
      },
      409,
    );
  }
  return ctx;
}

// `branchId` is not part of most mutation requests, so the HTTP layer infers
// it from the most recently cached branch. This mirrors the Cashier page
// contract: a single branch is active in the UI at a time.
let lastBranchId: string | null = null;

function setLastBranch(branchId: string): void {
  lastBranchId = branchId;
}

function currentBranchId(): string {
  if (!lastBranchId) {
    throw new ApiError(
      {
        type: 'petota:biz-cashier/branch-not-resolved',
        title: 'Cashier branch not resolved',
        status: 409,
        detail:
          'No branch has been registered for the cashier yet. Load a snapshot first.',
      },
      409,
    );
  }
  return lastBranchId;
}

// ---------------------------------------------------------------------------
// Error taxonomy
// ---------------------------------------------------------------------------

function translate(err: unknown, fallback: string): never {
  if (err instanceof ApiError) {
    const detail = err.payload.detail ?? err.payload.title;
    if (detail && err.message !== detail) {
      (err as { message: string }).message = detail;
    }
    throw err;
  }
  throw new Error(`${fallback}: ${(err as Error)?.message ?? String(err)}`);
}

// ---------------------------------------------------------------------------
// BE → FE shape adapters
// ---------------------------------------------------------------------------

/** Minimal superset of the BE session row (projection of what we use). */
interface BeSessionRow {
  readonly id: string;
  readonly branchId: string;
  readonly openedByStaffId: string | null;
  readonly openedByActorId: string;
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly status: CashierSession['status'];
  readonly cashFloatMinor: number;
  readonly ordersCount: number;
  readonly grossMinor: number;
  readonly cashCollectedMinor: number;
}

/** Minimal superset of the BE order. */
interface BeOrderLine {
  readonly lineId: string;
  readonly itemId: string | null;
  readonly label: string;
  readonly qty: number;
  readonly unitPriceMinor: number;
  readonly taxRate: number;
  readonly lineTotalMinor: number;
  readonly discount: CashierOrderLineDiscount | null;
  readonly note: string | null;
}

interface BeOrder {
  readonly id: string;
  readonly sessionId: string;
  readonly status: CashierOrder['status'];
  readonly customerId: string | null;
  readonly customerName: string | null;
  readonly petId: string | null;
  readonly petName: string | null;
  readonly subtotalMinor: number;
  readonly orderDiscountMinor: number;
  readonly taxMinor: number;
  readonly totalMinor: number;
  readonly lines: readonly BeOrderLine[];
  readonly tenders: readonly CashierTender[];
  readonly parkedLabel: string | null;
  readonly openedAt: string;
  readonly closedAt: string | null;
}

interface BeSnapshotEnvelope {
  readonly branchId: string;
  readonly session: BeSessionRow;
  readonly activeOrder: BeOrder;
  readonly parkedOrders: readonly BeOrder[];
}

function adaptLine(line: BeOrderLine): CashierOrderLine {
  return {
    lineId: line.lineId,
    itemId: line.itemId ?? '',
    label: line.label,
    qty: line.qty,
    unitPriceMinor: line.unitPriceMinor,
    lineTotalMinor: line.lineTotalMinor,
    taxRate: line.taxRate,
    discount: line.discount,
    note: line.note,
  };
}

function adaptOrder(order: BeOrder): CashierOrder {
  return {
    id: order.id,
    sessionId: order.sessionId,
    customerId: order.customerId,
    customerName: order.customerName,
    petId: order.petId,
    petName: order.petName,
    lines: order.lines.map(adaptLine),
    subtotalMinor: order.subtotalMinor,
    orderDiscountMinor: order.orderDiscountMinor,
    taxMinor: order.taxMinor,
    totalMinor: order.totalMinor,
    tenders: order.tenders.map((t) => ({
      method: t.method,
      amountMinor: t.amountMinor,
      reference: t.reference ?? null,
      paymentIntentId: t.paymentIntentId ?? null,
    })),
    status: order.status,
    parkedLabel: order.parkedLabel,
    openedAt: order.openedAt,
    closedAt: order.closedAt,
  };
}

function adaptSession(row: BeSessionRow): CashierSession {
  return {
    id: row.id,
    openedByStaffId: row.openedByStaffId ?? row.openedByActorId,
    openedByName: row.openedByStaffId ?? row.openedByActorId ?? 'Kasiyer',
    openedAt: row.openedAt,
    closedAt: row.closedAt,
    status: row.status,
    cashFloatMinor: row.cashFloatMinor,
    ordersCount: row.ordersCount,
    grossMinor: row.grossMinor,
    cashCollectedMinor: row.cashCollectedMinor,
  };
}

function buildSnapshot(
  branchId: string,
  env: BeSnapshotEnvelope,
  catalog: CatalogPayloadResponse,
): CashierSnapshot {
  return {
    branchId,
    branchName: 'Şube',
    session: adaptSession(env.session),
    activeOrder: adaptOrder(env.activeOrder),
    parkedOrders: env.parkedOrders.map(adaptOrder),
    categories: catalog.categories,
    catalog: catalog.catalog,
    availableTenders: catalog.availableTenders,
    updatedAt: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Snapshot (GET /biz/cashier/snapshot?branchId=)
//
// Snapshot + catalog are fetched in parallel; catalog hits a 60s cache after
// the first call so cart mutations don't refetch the price list each time.
// ---------------------------------------------------------------------------

async function fetchSnapshot(branchId: string): Promise<CashierSnapshot> {
  const [envelope, catalog] = await Promise.all([
    bizGet<BeSnapshotEnvelope>('/biz/cashier/snapshot', {
      params: { branchId },
    }),
    fetchCatalog(branchId),
  ]);
  rememberBranchCtx(branchId, {
    sessionId: envelope.session.id,
    activeOrderId: envelope.activeOrder.id,
  });
  setLastBranch(branchId);
  return buildSnapshot(branchId, envelope, catalog);
}

/** Re-fetch + re-project. Mutations call this to return a fresh snapshot so
 * `useCashier`'s single-setQueryData flow stays identical to the mock. */
async function refetchSnapshot(): Promise<CashierSnapshot> {
  return fetchSnapshot(currentBranchId());
}

export async function getCashierSnapshot(
  params: GetCashierSnapshotParams,
): Promise<CashierSnapshot> {
  try {
    return await fetchSnapshot(params.branchId);
  } catch (err) {
    translate(err, 'getCashierSnapshot failed');
  }
}

// ---------------------------------------------------------------------------
// Cart mutations
// ---------------------------------------------------------------------------

export async function addCartItem(
  req: AddCartItemRequest,
): Promise<CashierSnapshot> {
  const branchId = currentBranchId();
  const ctx = requireBranchCtx(branchId);
  const catalogItem = await resolveCatalogItem(branchId, req.itemId);
  if (!catalogItem) {
    throw new ApiError(
      {
        type: 'petota:biz-cashier/catalog-item-not-found',
        title: 'Catalog item not found',
        status: 404,
        detail: `No catalog row for item id ${req.itemId} on the current branch.`,
      },
      404,
    );
  }
  try {
    await bizPost(`/biz/cashier/orders/${ctx.activeOrderId}/lines`, {
      kind: catalogItem.kind,
      itemSourceType:
        catalogItem.kind === 'service'
          ? 'service'
          : catalogItem.kind === 'product'
            ? 'product'
            : 'manual',
      itemSourceId: catalogItem.id,
      sku: catalogItem.sku ?? null,
      label: catalogItem.label,
      unitPriceMinor: catalogItem.priceMinor,
      qty: Math.max(1, req.qty),
      unit: catalogItem.unit,
      taxRateBp: Math.round(catalogItem.taxRate * 10_000),
      note: req.note ?? null,
    });
    return await refetchSnapshot();
  } catch (err) {
    translate(err, 'addCartItem failed');
  }
}

/**
 * Locates the catalog row for `itemId` on the active branch. Tries the cached
 * catalog first; if the item is missing, forces a single refetch (e.g. an admin
 * just added a new SKU between snapshot loads) before giving up.
 */
async function resolveCatalogItem(
  branchId: string,
  itemId: string,
): Promise<CashierCatalogItem | null> {
  const cached = catalogCache.get(branchId)?.payload;
  const fromCache = cached?.catalog.find((i) => i.id === itemId);
  if (fromCache) return fromCache;
  const fresh = await fetchCatalog(branchId, { force: true });
  return fresh.catalog.find((i) => i.id === itemId) ?? null;
}

export async function updateCartQty(
  req: UpdateCartQtyRequest,
): Promise<CashierSnapshot> {
  const ctx = requireBranchCtx(currentBranchId());
  try {
    await bizPatch(
      `/biz/cashier/orders/${ctx.activeOrderId}/lines/${req.lineId}`,
      { qty: req.qty },
    );
    return await refetchSnapshot();
  } catch (err) {
    translate(err, 'updateCartQty failed');
  }
}

export async function removeCartItem(
  req: RemoveCartItemRequest,
): Promise<CashierSnapshot> {
  const ctx = requireBranchCtx(currentBranchId());
  try {
    await bizDelete(
      `/biz/cashier/orders/${ctx.activeOrderId}/lines/${req.lineId}`,
    );
    return await refetchSnapshot();
  } catch (err) {
    translate(err, 'removeCartItem failed');
  }
}

export async function applyDiscount(
  req: ApplyDiscountRequest,
): Promise<CashierSnapshot> {
  const ctx = requireBranchCtx(currentBranchId());
  try {
    if (req.lineId === null) {
      await bizPost(`/biz/cashier/orders/${ctx.activeOrderId}/discount`, {
        kind: req.kind,
        valueMinor: req.valueMinor,
        reasonCode: req.reasonCode,
      });
    } else {
      await bizPost(`/biz/cashier/orders/${ctx.activeOrderId}/line-discount`, {
        lineId: req.lineId,
        kind: req.kind,
        valueMinor: req.valueMinor,
        reasonCode: req.reasonCode,
      });
    }
    return await refetchSnapshot();
  } catch (err) {
    translate(err, 'applyDiscount failed');
  }
}

// ---------------------------------------------------------------------------
// Order lifecycle
// ---------------------------------------------------------------------------

export async function parkOrder(
  req: ParkOrderRequest,
): Promise<CashierSnapshot> {
  const ctx = requireBranchCtx(currentBranchId());
  try {
    await bizPost(`/biz/cashier/orders/${ctx.activeOrderId}/park`, {
      label: req.label,
    });
    // Snapshot endpoint will auto-create a fresh empty `open` order when all
    // tickets are parked — FE contract preserved.
    return await refetchSnapshot();
  } catch (err) {
    translate(err, 'parkOrder failed');
  }
}

export async function resumeParked(
  req: ResumeParkedRequest,
): Promise<CashierSnapshot> {
  try {
    await bizPost(`/biz/cashier/orders/${req.orderId}/resume`, {});
    return await refetchSnapshot();
  } catch (err) {
    translate(err, 'resumeParked failed');
  }
}

export async function voidOrder(
  req: VoidOrderRequest,
): Promise<CashierSnapshot> {
  const ctx = requireBranchCtx(currentBranchId());
  const rawReason = (req.reason ?? '').trim();
  const reasonCode = rawReason.length > 0 ? rawReason.split(':')[0]!.trim() : 'manual';
  const explanation =
    rawReason.length >= 20
      ? rawReason
      : `${rawReason || 'Kasada iptal'} — FE void flow (cashier page).`;
  try {
    await bizPost(`/biz/cashier/orders/${ctx.activeOrderId}/void`, {
      reasonCode,
      explanation,
    });
    return await refetchSnapshot();
  } catch (err) {
    translate(err, 'voidOrder failed');
  }
}

export async function commitTender(
  req: CommitTenderRequest,
): Promise<CommitTenderResult> {
  const ctx = requireBranchCtx(currentBranchId());
  try {
    const result = await bizPost<{
      order: BeOrder;
      invoiceId: string;
      committedAt: string;
    }>(`/biz/cashier/orders/${ctx.activeOrderId}/commit`, {
      tenders: req.tenders.map((t) => ({
        method: t.method,
        amountMinor: t.amountMinor,
        reference: t.reference ?? null,
        // ADR-109 — bridge card tenders that went through the BE 3DS gateway
        // to their payment_intents row. Only meaningful for `method='card'`;
        // route layer asserts UUID + method='card' for non-null values.
        paymentIntentId: t.paymentIntentId ?? null,
      })),
      idempotencyKey: `biz:cashier:commit:${ctx.activeOrderId}`,
    });
    return {
      order: adaptOrder(result.order),
      receiptId: result.invoiceId,
      committedAt: result.committedAt,
    };
  } catch (err) {
    translate(err, 'commitTender failed');
  }
}

export async function closeSession(
  req: CloseSessionRequest,
): Promise<CashierSnapshot> {
  const ctx = requireBranchCtx(currentBranchId());
  const countedCash = sumCountedCash(req.counts);
  try {
    await bizPost(`/biz/cashier/sessions/${ctx.sessionId}/close`, {
      countedCashMinor: countedCash,
      reconciliationCounts: req.counts,
      notes: null,
    });
    return await refetchSnapshot();
  } catch (err) {
    translate(err, 'closeSession failed');
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * `counts` is a flexible map of denomination → count (e.g. `{ "200": 2, "100": 5 }`)
 * or simply `{ total: 87250 }` (minor units). We try denomination semantics
 * first (key × value), then fall back to a direct `total` field, then to
 * summing all values as minor units.
 */
function sumCountedCash(counts: Readonly<Record<string, number>>): number {
  if (!counts || Object.keys(counts).length === 0) return 0;
  if (typeof counts['total'] === 'number' && Number.isFinite(counts['total'])) {
    return Math.max(0, Math.round(counts['total']));
  }
  let sum = 0;
  for (const [denom, count] of Object.entries(counts)) {
    const denomMinor = Number(denom);
    if (Number.isFinite(denomMinor) && denomMinor > 0 && Number.isFinite(count)) {
      sum += Math.round(denomMinor * count);
    } else if (Number.isFinite(count)) {
      sum += Math.max(0, Math.round(count));
    }
  }
  return Math.max(0, sum);
}
